// @flow

import { v4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import type { Node, RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { SSRContext } from 'shared_data/providers/ssr/Context';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { cmsViewFetch } from 'shared_services/redux/actions/cms/cms';

type Props = {
  loading: boolean,
  data: Array<Object>,
};

/**
 * CmsViewsProvider
 *
 * @param {Props} props
 * @returns Node
 */
export function CmsViewsProvider({ loading, data }: Props): Node {
  const dispatch: Function = useDispatch();
  const loaderUniqueId = useRef(v4());
  const previousData: RefObject<Object> = useRef(null);
  const { isSSR } = useContext(SSRContext);

  /**
   * handleUpdate
   */
  const handleUpdate = useCallback(() => {
    if (data && !isEqual(previousData.current, data)) {
      // Store current data in ref
      previousData.current = data;

      const mappedData = data.reduce((accumulator, { placeholder, module, content }) => {
        accumulator[placeholder] = {
          module,
          content: JSON.parse(content),
        };
        return accumulator;
      }, {});

      if (mappedData) {
        dispatch(cmsViewFetch(mappedData));
      }
    }
  }, [data, dispatch]);

  // Called on server
  if (isSSR) {
    handleUpdate();
  }

  // Called on client only
  useEffect(() => {
    handleUpdate();
  }, [handleUpdate]);

  return <DataProviderPreloaderPage uniqueId={loaderUniqueId.current} attach={loading} />;
}
